import Konva from "konva";
import { clamp } from "lodash";
import { UnitSystem } from "shared/util/units";
import { layerNames } from "./editorConstants";
import { gridGeometry, PlanSize } from "./gridGeometry";

// The plan image the offer carries is a document, not a screenshot of somebody's browser. None
// of what the person happens to be looking at may reach it: not the window size, not how far
// they zoomed, not where they panned to, not whether the pointer was over the canvas at the
// moment they pressed the button. So the frame is taken from the plan's own geometry - the very
// box "fit to screen" measures against, background margin and grid numbers included - and the
// resolution is asked for in output pixels rather than read off the screen.

// The longer side of the exported image. Wide enough for ~300 DPI across the frame the offer
// PDF gives the plan, and no wider: past that the tile textures have no more detail to give.
export const captureLongSide = 760;
// A small plan would ask for a huge ratio to fill that; there is nothing there to magnify.
const maxPixelRatio = 6;
// The canvas is captured at its own 1:1 scale, and it has to be a fixed one: the plan border
// and the grid lines are drawn divided by the zoom so that they keep their thickness on screen,
// so at any other scale they would come out of the export thicker or thinner than the plan.
export const captureScale = 1;

export type CaptureOptions = {
  plan: PlanSize;
  unit: UnitSystem;
  // What shows through wherever the plan does not reach. The background layer is a rectangle of
  // the plan's own size, so the closing grid line can fall past its edge - and a PNG with a
  // transparent strip down its side turns black in the PDF.
  backgroundColor: string;
  showGrid: boolean;
};

// The stage has to be standing at `captureScale`, at its origin, before this is called - the
// caller owns that, because React owns the props that are divided by the zoom (see capturePlan
// in EditorContext). Then the plan's coordinates are the export's coordinates.
export function capturePlanImage(stage: Konva.Stage, { plan, unit, backgroundColor, showGrid }: CaptureOptions) {
  const { right, bottom } = gridGeometry(plan, unit);
  const pixelRatio = clamp(captureLongSide / Math.max(right, bottom), 0, maxPixelRatio);

  const grid = stage.findOne<Konva.Layer>(`.${layerNames.grid}`);
  // the mouse cross-hair with its coordinate chip, and the tile hanging off the cursor: both
  // are answers to where the pointer is, and there is no pointer in a PDF
  const helpers = [layerNames.tracker, layerNames.ghost]
    .map(name => stage.findOne<Konva.Layer>(`.${name}`))
    .filter((layer): layer is Konva.Layer => !!layer);

  const previous = [grid, ...helpers].map(layer => ({ layer, visible: layer?.visible() }));

  helpers.forEach(layer => layer.visible(false));
  grid?.visible(showGrid);

  try {
    const source = stage.toCanvas({ x: 0, y: 0, width: right, height: bottom, pixelRatio });

    const target = document.createElement("canvas");
    target.width = source.width;
    target.height = source.height;

    const context = target.getContext("2d");
    if (!context) {
      return "";
    }

    context.fillStyle = backgroundColor;
    context.fillRect(0, 0, target.width, target.height);
    context.drawImage(source, 0, 0);

    return target.toDataURL("image/png");
  } finally {
    previous.forEach(({ layer, visible }) => layer?.visible(!!visible));
  }
}
